import React, {useState, useContext, useMemo, useReducer, useEffect} from 'react';
import axios from 'axios';
import uuid from 'react-uuid';
import moment from "moment";
import {Link, useParams} from 'react-router-dom';
import {Alert, Button, Card, Col, Form, Row} from 'react-bootstrap';
import {useLocalStorage} from 'react-use';
import AuditContext from '../Utils/context.js';
import {ACTIONS} from '../Utils/action.js';
import {useAlert, TYPES} from 'hooks/use-alert-hook.js';
import AuditorMinutes from '../Modal/Minutes/Auditor.js';
import EventContext from './Visitation/context';
import EventReducer from './Visitation/reducer';
import EventModalReducer from './components/Event/reducer';
import {ACTIONS as EVENT_ACTION} from './components/Event/action';

import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import { set } from 'date-fns';
// react component used to create alerts
import Event from './components/Event.js';


const defaultPopupValue = {
  uuid: uuid(),
  title: ``,
  start: moment(),
  end: moment(),
  reminders: []
}

export default function Visitation()
{

  const {employeeId, scheduleId} = useParams();
  const localizer = momentLocalizer(moment);

  const {schedule, dispatchSchedule} = useContext(AuditContext);
  const [events, dispatchEvents] = useReducer(EventReducer, []);
  const [popup, dispatchPopup] = useReducer(EventModalReducer, defaultPopupValue);

  const [auth] = useLocalStorage('authenticated');
  const [swal, setSwal] = useAlert(null);
  const [timeMin, setTimeMin] = useState(moment().startOf('month').toISOString());
  const [timeMax, setTimeMax] = useState(moment().endOf('month').toISOString());
  const [showMinutes, setShowMinutes] = useState(false);
  const [showCalender, setShowCalender] = useState(false);
  const [showEvent, setShowEvent] = useState(false);

  const memeo = useMemo(() => showEvent, [events]);

  const canUpdate = () => {

    if (!['VISITED', 'APPROVED', 'COMPLETED'].includes(schedule.SCHEDULE_STATUS)) {

      // if not all forms submitted
      if (schedule.forms.some(form => !form.VISIT_AT || !form.VISIT_BY)) {
        return <Alert variant="info" style={{textAlign: 'center'}}>Visitation is in Process. Please complete all forms.</Alert>;
      }

      // if not head of audit
      if (schedule.auditorTeams.find(member => member.AUDITOR_TYPE_ID === 2)?.auditor?.EMP_ID !== employeeId) {
        return <Alert variant="info" style={{textAlign: 'center'}}>Waiting for Head of Auditor to create a Minutes of Audit.</Alert>;
      }

      return <Button variant="primary" onClick={handleDoneMinutes}>Write a summary to complete the Audit</Button>;
    }

    if (!['EVALUATED', 'APPROVED', 'COMPLETED'].includes(schedule.SCHEDULE_STATUS)) {
      return <Alert variant="info" style={{textAlign: 'center'}}>Waiting for BPMI Approval.</Alert>;
    }

    if (!['EVALUATED', `VISITED`, 'COMPLETED'].includes(schedule.SCHEDULE_STATUS)) {
      return <Alert variant="info" style={{textAlign: 'center'}}>Visitation Process has been completed.</Alert>;
    }
  }

  const handleDoneMinutes = () => {

    //validate state status
    if (schedule.SCHEDULE_STATUS !== 'EVALUATED') {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          title: `Failed!`,
          message: `Cannot jump to this step!`,
        }
      });

      return false;
    }

    setShowMinutes(<AuditorMinutes onHide={handleHideMinutes} onSubmit={handleSubmitMinutes} schedule={schedule}/>);

  }


  const handleSubmitMinutes = (auditorStatement) => {

    try {

      const onSuccessCallback = async () => {

        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}minutes`,
          {
            SCHEDULE_ID: schedule.SCHEDULE_ID,
            AUDITOR_NOTE: auditorStatement,
            CREATED_AT: moment().format('YYYY-MM-DD hh:mm:ss'),
            CREATED_BY: employeeId,
          },
          {
            headers: {Authorization: `Bearer ${auth.token}`},
            params: {expand: `schedule`},
          }
        );

        if (response.status == 201) {

          handleHideMinutes();
          dispatchSchedule({
            type: ACTIONS.EDIT,
            payload: {SCHEDULE_STATUS: response.data.schedule.SCHEDULE_STATUS},
          });
        }
      }

      const confirmDialog = confirm("Are you sure?");
      if (confirmDialog) {
        onSuccessCallback();
      }

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }

  }

  const getVariant = (form) => {

    switch (true) {
      case (form?.VISIT_AT !== null && form?.VISIT_BY !== null):
        return 'success';
      case (form?.EVALUATE_AT !== null && form?.EVALUATE_BY !== null):
        return 'primary';
      default:
        return 'secondary';
    }
  }

  const handleHideMinutes = () => {
    setShowMinutes(null);
  }

  const handleTagCalender = () => {
    setShowCalender(true)
  }

  const fetchEvents = async () => {

    let response = await axios.get(
      `${process.env.REACT_APP_API_HOST}calendars`,
      {
        headers: {Authorization: `Bearer ${auth.token}`},
        params: {
          email: `siami@umn.ac.id`,
          timeMin,
          timeMax,
        }
      }
    );

    dispatchEvents({
      type: ACTIONS.FETCH,
      payload: response.data,
    });

  }

  const handleRemoveEvent = async (id) => {
    try{

      const response = await axios.delete(
        `${process.env.REACT_APP_API_HOST}schedule-visits/${id}`,
          { headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
      );

      if (response.status === 200){
        fetchEvents();
        handleHideEvent();
      }else{
        console.log(response);
      }

    }catch(ex){
      console.log(ex);
      window.alert(ex.toString());
    }

  }

  const handleSubmitEvent = async (
    title,
    notificationType,
    dateValue,
    dateEnd,
    timeStart,
    timeEnd,
    isAllDay,
    notificationValue,
    reminderType,
    id,
    googleCalendarId
  ) => {

    let notificationMinutes = 1;
    // reminder
    switch (true) {
      case notificationType === 'hours' :
        notificationMinutes = 60;
        break;
      case notificationType === 'days' :
        notificationMinutes = 24 * 60;
        break;
    }

    // date time
    let startPeriod = '';
    let endPeriod = '';

    switch (true) {
      case isAllDay :
        startPeriod = moment(dateValue).format('YYYY-MM-DD') + 'T00:00:00-07:00';
        endPeriod = moment(dateEnd).format('YYYY-MM-DD') + 'T00:00:00-07:00';
        break;

      case !isAllDay :
        startPeriod = moment(dateValue).format('YYYY-MM-DD') + 'T' + timeStart + ':00-07:00';
        endPeriod = moment(dateEnd).format('YYYY-MM-DD') + 'T' + timeEnd + ':00-07:00';
        break;
    }


    try {
      let payload = {
        'VISIT_ID': id,
        "SCHEDULE_ID" : schedule.SCHEDULE_ID,
        "TITLE" : title,
        "START_DATE": dateValue,
        "TIME_START": timeStart,
        "TIME_END": timeEnd,
        "END_DATE": dateEnd,
        "ALL_DAY": isAllDay ? 1 : 0,
        'REMINDER_TYPE': reminderType,
        'NOTIFICATION_TYPE': notificationType,
        'NOTIFICATION_VALUE': notificationValue,
        GOOGLE_CALENDAR_ID: googleCalendarId,
        CREATED_AT: moment().format('YYYY-MM-DD hh:mm:ss'),
        CREATED_BY: auth.employee.EMP_ID,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}schedule-visits`, payload,
        {
          headers: {Authorization: `Bearer ${auth.token}`},
        }
      );

      if (response.status == 201 || response.status == 200) {
        handleHideEvent();
        fetchEvents();
      }else{
        window.alert('Uupps, something error during insert event to database');
        console.log(response);
      }

    } catch (error) {
      console.log(error);
      window.alert(error);
    }
  }

  const handleSelectEvent = ({id}) => {


    const event = events.find(event => event.id === id);

    dispatchPopup({
      type: EVENT_ACTION.UPDATE,
      payload: event
    });

    setShowEvent(
      <Event
        event={event}
        scheduleId={scheduleId}
        onHide={handleHideEvent}
        onSubmit={handleSubmitEvent}
        onRemove={handleRemoveEvent}
      />
    );
  };

  const handleSelectSlot = ({start, end}) => {

    dispatchPopup({
      type: EVENT_ACTION.UPDATE,
      payload: {
        start: moment(start),
        end: moment(end),
      }
    });

    setShowEvent(<Event
      scheduleId={scheduleId}
      onHide={handleHideEvent}
      onSubmit={handleSubmitEvent}
    />);
  };

  const handleRangeChange = e => {
    setTimeMin(moment(e.start).toISOString());
    setTimeMax(moment(e.end).toISOString())
  }

  const handleHideEvent = () => {
    setShowEvent(null);
    dispatchPopup({
      type: EVENT_ACTION.UPDATE,
      payload: defaultPopupValue
    });
  }

  const eventColors = (event, start, end, isSelected) => {
    var backgroundColor = "rbc-event-";
    event.color
      ? (backgroundColor = backgroundColor + event.color)
      : (backgroundColor = backgroundColor + "default");
    return {
      className: backgroundColor,
    };
  };

  useEffect(() => {
    console.log('useEffect')
    fetchEvents();
  }, [timeMin, timeMax]);


  return (
    <EventContext.Provider value={{
      events, dispatchEvents,
      popup, dispatchPopup
    }}>
      {console.log('event:', events)}
      {console.log('popup:', popup)}
      {swal}{showMinutes}{showEvent}
      <h3>Visitation</h3>
      {!showCalender ? <a><Button onClick={handleTagCalender} >Tag Your Calendar</Button><hr /></a> : ''}
      {showCalender ? <Button onClick={fetchEvents} variant="success">Refresh Calendar</Button> :''}
      {showCalender ?
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <BigCalendar
                  selectable={true}
                  localizer={localizer}
                  events={events}
                  defaultView="month"
                  scrollToTime={new Date(1970, 1, 1, 6)}
                  defaultDate={new Date()}
                  onRangeChange={handleRangeChange}
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={(slotInfo) => handleSelectSlot(slotInfo)}
                  eventPropGetter={eventColors}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      : ''}
      <Form.Group>
        <Row>
          {schedule.forms && schedule.forms.map((form, key) => (
            <Col key={key} md={4}>
              <Link to={`/auditor/${employeeId}/visitations/${form.FORM_ID}`}>
                <Button variant={getVariant(form)} style={{textAlign: 'left'}}>{form.criteria.CRITERIA_CODE}. {form.criteria.CRITERIA_LABEL}</Button> {' '}
              </Link>
            </Col>
          ))}
        </Row>
      </Form.Group>
      <Form.Group>
        {canUpdate()}
      </Form.Group>
    </EventContext.Provider>
  )
}